/**
 * src/services/agentNarrationService.ts
 *
 * Client for the backend `/api/agent-narration` endpoint, which uses
 * GeoLink's AI assistant (Azure AI Foundry) to generate lively
 * first-person narration lines for an agent's teleport auto-broadcast.
 * The backend always returns a usable line (AI when available, a
 * deterministic template otherwise), so callers don't need their own
 * fallback — but a network failure here still throws, and the caller
 * (the broadcast hook) degrades to its local template.
 */

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

export interface NarrationObject {
  name: string;
  type?: string;
}

export interface NarrationInput {
  place?: string | null;
  country?: string | null;
  agentName?: string | null;
  objects?: NarrationObject[];
  recentLines?: string[];
  intro?: boolean;
}

export interface NarrationResult {
  text: string;
  source: 'ai' | 'fallback';
}

/**
 * Ask the backend for the next narration line. `timeoutMs` guards
 * against a slow AI call stalling the broadcast cadence — on timeout
 * the promise rejects and the caller uses its own line.
 */
export async function generateNarration(
  input: NarrationInput,
  opts: { backend?: string; timeoutMs?: number } = {},
): Promise<NarrationResult> {
  const base = (opts.backend ?? DEFAULT_BACKEND).replace(/\/$/, '');
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? 6000);
  try {
    const res = await fetch(`${base}/api/agent-narration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      signal: ctl.signal,
    });
    if (!res.ok) throw new Error(`narration_failed_${res.status}`);
    const data = await res.json();
    return { text: String(data.text || ''), source: data.source === 'ai' ? 'ai' : 'fallback' };
  } finally {
    clearTimeout(timer);
  }
}
